import React, { useCallback } from 'react';

import { SyncOutlined } from '@ant-design/icons';
import {
  createNotification,
  ModernActionButton,
} from '@fastlessjs/backoffice-layer/dist/components/atoms';
import { useAuth } from '@fastlessjs/backoffice-layer/dist/hooks';
import { useQueryClient } from '@tanstack/react-query';

import {
  RefreshPayload,
  RefreshResult,
  useRefreshEntity,
} from '../hooks/useRefreshEntity';

export type EurekaRefreshButtonProps = {
  entity: RefreshPayload['entity'];
  items?: Array<Record<string, unknown>>;
  filter?: Record<string, unknown>;
  /** Each entry is a full React Query key (e.g. `['orders', clientId]`). */
  queryKeysToInvalidate: unknown[][];
  mode?: 'header' | 'row';
  disabled?: boolean;
};

function isSkipped(r: RefreshResult): boolean {
  return 'skipped' in r && r.skipped === true;
}

function isFailed(r: RefreshResult): boolean {
  return 'eurekaOk' in r && r.eurekaOk === false;
}

function isOkChanged(r: RefreshResult): boolean {
  return 'eurekaOk' in r && r.eurekaOk === true && 'changed' in r && r.changed === true;
}

const EurekaRefreshButton: React.FC<EurekaRefreshButtonProps> = ({
  entity,
  items,
  filter,
  queryKeysToInvalidate,
  mode = 'header',
  disabled,
}) => {
  const godMode = useAuth((state) => state.godMode);
  const isSuperAdminConsole = useAuth((state) => state.getIsSuperAdminConsole());

  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useRefreshEntity();

  const handleRefresh = useCallback(async () => {
    const payload: RefreshPayload = { entity };
    if (items) payload.items = items;
    if (filter !== undefined) payload.filter = filter;

    const data = await mutateAsync(payload);
    const results = data?.results ?? [];

    await Promise.all(
      queryKeysToInvalidate.map((queryKey) =>
        queryClient.invalidateQueries({ queryKey: queryKey as readonly unknown[] }),
      ),
    );

    const failed = results.filter(isFailed);
    const skipped = results.filter(isSkipped);
    const changed = results.filter(isOkChanged);

    if (failed.length > 0) {
      createNotification(
        'warning',
        'Partial sync',
        `${failed.length} item(s) could not be synced.`,
      );
    }

    if (changed.length > 0) {
      createNotification(
        'success',
        'Sync complete',
        `${changed.length} item(s) updated.`,
      );
    } else if (skipped.length > 0) {
      createNotification('info', 'Already fresh', 'Data is still fresh (< 1h).');
    } else if (results.length > 0 && failed.length === 0) {
      createNotification('info', 'Up to date', 'Data is already up to date.');
    }
  }, [entity, items, filter, mutateAsync, queryClient, queryKeysToInvalidate]);

  if (!godMode || !isSuperAdminConsole) return null;

  const isRowMode = mode === 'row';
  const label = filter !== undefined ? 'Refresh all' : 'Refresh';
  const title = isRowMode ? 'Refresh item' : label;

  return (
    <ModernActionButton
      title={title}
      icon={<SyncOutlined style={{ color: '#1890ff' }} />}
      onClick={() => {
        handleRefresh().catch(() => {});
      }}
      loading={isPending}
      disabled={disabled || isPending}
      variant="secondary"
      size={isRowMode ? 'sm' : 'md'}
    >
      {!isRowMode ? label : undefined}
    </ModernActionButton>
  );
};

export default EurekaRefreshButton;
